<template>
  <app-page class="user-page" fill lean>
    <!-- Kopf wie die übrigen Verwaltungsseiten (Kundenwunsch 13.10.2026): leichter Titel, rechts die Aktionen mit Icon
         und Beschriftung (Handy: Icon-Knöpfe) -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
      <app-button icon="plus" @click="addUser">Benutzer anlegen</app-button>
    </template>
    <!-- Werkzeugleiste: links die Suche, rechts der Filter mit Status („Aktiv“ vorbelegt) und Rolle (Panel wie Verkäufe
         statt Umschalter und Auswahl in der Leiste) -->
    <template #toolbar>
      <app-search
        v-model="searchText"
        placeholder="Name oder Benutzername"
        :width="260"
        @input="onSearchInput"
      />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :active-filters="activeFilterCount"
        :result-nouns="resultNouns"
        :empty-value="emptyUserFilters"
        :show-range="false"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <div class="user-gridcol">
      <div class="app-grid-fill user-grid" :class="{ 'user-grid--auto': handy }">
        <dx-data-grid
          id="gridContainer"
          :ref="gridRefName"
          :height="gridHeight"
          :data-source="userDataSource"
          :columns="columns"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :hover-state-enabled="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :allow-column-reordering="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :focused-row-enabled="true"
          :on-data-error-occurred="onDataErrorOccurred"
          @focused-row-changing="onFocusedRowChanging"
          @focused-row-changed="onFocusedRowChanged"
          @row-prepared="onRowPrepared"
          @content-ready="onContentReady"
        >
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
          <dx-filter-row :visible="false" />
          <dx-search-panel :visible="false" />
          <dx-selection mode="none" />
          <template #nameZelle="{ data }">
            <div class="user-cell">
              <app-avatar :name="data.data.fullname || data.data.username" />
              <div class="user-cell__text">
                <span class="user-cell__name">
                  <span class="user-cell__fullname">{{ data.data.fullname || "–" }}</span>
                </span>
                <span class="user-cell__login">{{ data.data.username }}</span>
              </div>
            </div>
          </template>
          <!-- Status deutlich (Kundenwunsch 13.10.2026): Punkt mit „Aktiv“/„Inaktiv“, dahinter der Tag „Test“ -->
          <template #statusZelle="{ data }">
            <span class="user-status">
              <span
                class="user-status__dot"
                :class="data.data.active == 1 ? 'user-status__dot--on' : 'user-status__dot--off'"
                aria-hidden="true"
              ></span>
              <span class="user-status__text">{{ data.data.active == 1 ? "Aktiv" : "Inaktiv" }}</span>
              <app-tag v-if="data.data.is_test_user == 1" kind="test" />
            </span>
          </template>
          <template #rolleZelle="{ data }">
            <span class="user-role">{{ data.text || data.value || "–" }}</span>
          </template>
          <template #passwortKopf>
            <span>Passwort</span>
          </template>
          <template #passwortZelle="{ data }">
            <span v-if="data.value === 'FORCE_CHANGE_PASSWORD'" class="user-pw user-pw--open">
              <app-icon name="clock" :size="16" />Erstanmeldung offen
            </span>
            <span v-else class="user-pw">{{ passwordText(data.value) }}</span>
          </template>
          <template #bemerkungZelle="{ data }">
            <span class="user-muted">{{ data.value || "–" }}</span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <template v-if="usersLoaded">
            <span><strong>{{ countNumber }}</strong> {{ countLabel }}</span>
            <span class="app-sep" aria-hidden="true">·</span>
            <span>davon <strong>{{ firstLoginNumber }}</strong> mit offener Erstanmeldung</span>
          </template>
        </template>
      </app-grid-footer>

      <app-dialog v-model="dialog" persistent title="Passwort ändern">
        <div class="app-form-field">
          <label class="app-label" for="user-neues-passwort">Neues Passwort</label>
          <app-text-field id="user-neues-passwort" v-model="newPassword" required autocomplete="off" />
        </div>
        <template #actions>
          <app-button @click="dialog = false">Abbrechen</app-button>
          <app-button kind="primary" @click="savePassword">Ändern</app-button>
        </template>
      </app-dialog>

      <app-dialog :value="discardDialog" title="Änderungen verwerfen?" @input="onDiscardDialogInput">
        <p class="user-dialog__text">Die ungespeicherten Änderungen gehen verloren.</p>
        <template #actions>
          <app-button @click="keepEditing">Weiter bearbeiten</app-button>
          <app-button kind="danger" @click="confirmDiscard">Verwerfen</app-button>
        </template>
      </app-dialog>

      <app-toast v-model="snackbar" :timeout="6000" icon="alert" kind="error" :text="snackbarText" closable />
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!editMode"
        icon="users"
        :title="editMode === 'create' ? 'Benutzer anlegen' : 'Benutzer bearbeiten'"
        close-label="Bearbeiten schließen"
        :dirty="dirty"
        @close="onDetailClose"
        @layout-change="onLayoutChange"
      >
        <template #subtitle>
          <app-tag v-if="editMode === 'edit' && editRow && editRow.active != 1" kind="off" />
          <app-tag v-if="editMode === 'edit' && editRow && editRow.is_test_user == 1" kind="test" />
        </template>
        <user-editor
          v-if="editMode"
          :value="draft"
          :user="editRow"
          :mode="editMode"
          :roles="roles"
          :errors="errors"
          @field="onField"
          @change-password="changePassword"
        />
        <!-- Knöpfe immer in einer Zeile; „1 Änderung“ bzw. ein Fehler nur bei Bedarf als eigene Zeile mit Trennlinie
             darüber (Kundenwunsch 13.10.2026), der Screenreader hört den Stand weiter über die Statusmeldung -->
        <template #footer>
          <span class="app-sr-only" role="status">{{ saveError || changeText }}</span>
          <span
            v-if="saveError || dirty"
            class="user-foot__text"
            :class="{ 'user-foot__text--error': !!saveError }"
            aria-hidden="true"
          >
            <app-icon v-if="saveError" name="alert" :size="14" />{{ saveError || changeText }}
          </span>
          <app-button :disabled="!dirty || saving" @click="revertChanges">Verwerfen</app-button>
          <app-button kind="primary" icon="check" :disabled="!dirty" :loading="saving" @click="saveChanges">
            Speichern
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxFilterRow,
  DxSearchPanel,
  DxPager,
  DxSelection,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import query from "devextreme/data/query";
import axios from "axios";
import DataGridMixin from "@/mixins/DataGridMixin";
import { exportWithColumns } from "@/utils/gridExport";
import { formatNumber, plural } from "@/utils/format";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { emptyFilters, activeCount, buildPredicate, buildFacets, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { USER_FILTER_CONFIG } from "@/components/masterdata/masterFilters";
import UserEditor from "@/components/users/UserEditor.vue";

// Felder, die der Detailbereich speichert (Reihenfolge wie im bisherigen Batch-Editing)
const EDIT_FIELDS = ["fullname", "role", "comment", "active", "is_test_user"];
const CREATE_FIELDS = ["username", "user_id", "fullname", "role", "is_test_user"];
// Schalter: im Entwurf Boolean, zur API wie setCellValue als 1/0
const BOOLEAN_FIELDS = ["active", "is_test_user"];
// Weicht eine dieser Spalten nach dem Neuladen ab, übernimmt der Detailbereich (ohne offene Änderungen) den neuen Stand
const SYNC_FIELDS = ["fullname", "role", "comment", "active", "is_test_user", "password_status"];

function textValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

// null und "" gelten als gleich (leere Bemerkung), Schalter als Boolean
function sameValue(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return !!a === !!b;
  }
  return textValue(a) === textValue(b);
}

function emptyDraft() {
  return {
    username: "",
    user_id: "",
    fullname: "",
    role: "DRIVER",
    comment: "",
    active: true,
    is_test_user: false,
  };
}

export default {
  name: "User",
  components: {
    DxDataGrid,
    DxPaging,
    DxFilterRow,
    DxSearchPanel,
    DxPager,
    DxSelection,
    UserEditor,
    SaleFilterMenu,
  },
  mixins: [DataGridMixin],

  data() {
    return {
      dialog: false,
      gridRefName: "dataGrid",
      passwortChangeItem: null,
      newPassword: null,
      roles: [],
      searchText: "",
      gridAttr: { class: "grid-av" },
      // Filter-Panel (Kundenwunsch 13.10.2026): Status (Standard Aktiv) und Rolle, als Prüffunktion auf dem Grid
      filters: emptyFilters(USER_FILTER_CONFIG),
      filterSegments: USER_FILTER_CONFIG.segments,
      resultNouns: ["Benutzer", "Benutzer"],
      facets: [],
      resultCount: null,
      // Detailbereich: null | 'edit' | 'create'
      editMode: null,
      // gespeicherter Stand der bearbeiteten Zeile (eingefroren; Kopf, Schlüssel, Passwortdialog)
      editRow: null,
      draft: emptyDraft(),
      original: emptyDraft(),
      errors: {},
      saving: false,
      saveError: "",
      // Modus des offenen Detailbereichs ('side' | 'overlay' | 'full'), sonst null
      detailMode: null,
      discardDialog: false,
      snackbar: false,
      snackbarText: "",
      // Fußleiste
      usersLoaded: false,
      countTotal: 0,
      countFirstLogin: 0,

      userDataSource: new CustomStore({
        key: ["customer_id", "user_id", "username"],
        load: () =>
          axios.get(`${this.dataUrl}?customer_id=${this.$store.state.customerId}`).then((response) => {
            // vollständige Liste zusätzlich nicht reaktiv merken (nächste Benutzer-ID, Fußleiste, Detailbereich);
            // die Antwort geht unverändert an das Grid
            this._users = Array.isArray(response && response.data) ? response.data : [];
            this.refreshFacets();
            return response;
          }),
        insert: (newUser) =>
          axios.post(
            `${this.dataUrl}?customer_id=${this.$store.state.customerId}`,
            newUser
          ),
        update: (userKeys, userUpdates) =>
          axios.put(
            `${this.dataUrl}?customer_id=${userKeys.customer_id}&user_id=${userKeys.user_id}&username=${userKeys.username}`,
            userUpdates
          ),
        remove: (userKeys) =>
          axios.delete(
            `${this.dataUrl}?customer_id=${userKeys.customer_id}&user_id=${userKeys.user_id}&username=${userKeys.username}`
          ),
      }),
    };
  },
  computed: {
    dataUrl() {
      return `${this.$store.state.baseUrl}user`;
    },
    // Definitionsreihenfolge = Excel-Spalten (exportWithColumns, ohne „Status“); Bildschirm: Name, Rolle, Status,
    // Passwort, Bemerkung
    columns() {
      return [
        {
          dataField: "username",
          caption: "Benutzername",
          visible: false,
        },
        {
          dataField: "user_id",
          caption: "Benutzer ID",
          visible: false,
        },
        {
          dataField: "fullname",
          caption: "Name",
          alignment: "left",
          sortOrder: "asc",
          visibleIndex: 0,
          minWidth: 200,
          cellTemplate: "nameZelle",
        },
        {
          dataField: "comment",
          caption: "Bemerkung",
          visibleIndex: 4,
          minWidth: 120,
          cellTemplate: "bemerkungZelle",
        },
        {
          dataField: "role",
          caption: "Rolle",
          width: 140,
          visibleIndex: 1,
          cellTemplate: "rolleZelle",
          lookup: {
            dataSource: this.roles,
            valueExpr: "name",
            displayExpr: "description",
          },
        },
        {
          dataField: "is_test_user",
          caption: "Testbenutzer",
          dataType: "boolean",
          visible: false,
          setCellValue: function (rowData, value) {
            var newValue = value == true ? 1 : 0;
            this.defaultSetCellValue(rowData, newValue);
          },
        },
        {
          dataField: "active",
          caption: "Aktiv",
          dataType: "boolean",
          visible: false,
          setCellValue: function (rowData, value) {
            var newValue = value == true ? 1 : 0;
            this.defaultSetCellValue(rowData, newValue);
          },
        },
        {
          dataField: "password_status",
          caption: "Passwortstatus",
          dataType: "String",
          width: 184,
          visibleIndex: 3,
          headerCellTemplate: "passwortKopf",
          cellTemplate: "passwortZelle",
        },
        {
          // nur Bildschirm: Aktiv/Inaktiv und Test auf einen Blick (Kundenwunsch 13.10.2026; nicht im Export)
          name: "status",
          caption: "Status",
          width: 152,
          visibleIndex: 2,
          cellTemplate: "statusZelle",
          calculateCellValue: (row) => (row.active == 1 ? "Aktiv" : "Inaktiv") + (row.is_test_user == 1 ? " Test" : ""),
        },
      ];
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht (natürliche Höhe, Fensterscroll), das Grid wächst mit seinen Zeilen;
    // sonst füllt es die Spalte über der Fußleiste
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    activeFilterCount() {
      return activeCount(this.filters, USER_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, USER_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    changedFields() {
      if (!this.editMode) {
        return [];
      }
      const fields = this.editMode === "create" ? CREATE_FIELDS : EDIT_FIELDS;
      return fields.filter((key) => !sameValue(this.draft[key], this.original[key]));
    },
    dirty() {
      return this.changedFields.length > 0;
    },
    changeText() {
      const n = this.changedFields.length;
      if (!n) {
        return "Keine Änderungen";
      }
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
    countNumber() {
      return formatNumber(this.countTotal);
    },
    countLabel() {
      const n = this.countTotal;
      if (this.filters.status === "active") {
        return plural(n, "aktiver Benutzer", "aktive Benutzer");
      }
      if (this.filters.status === "inactive") {
        return plural(n, "inaktiver Benutzer", "inaktive Benutzer");
      }
      return "Benutzer";
    },
    firstLoginNumber() {
      return formatNumber(this.countFirstLogin);
    },
  },
  watch: {
    // neue Spaltendefinition (Rollen geladen): Bildschirmansicht erneut anwenden
    columns() {
      this.$nextTick(this.applyScreenView);
    },
    touch() {
      this.$nextTick(this.updateGridDimensions);
    },
    // Rollen geladen: Bezeichnungen der Liste „Rolle“ im Filter-Panel
    roles() {
      this.refreshFacets();
    },
  },
  created() {
    // nicht reaktiv: vollständig geladene Liste, Rückfrage-Fortsetzungen, Fokus durch Klick/Tastatur
    this._users = null;
    this.pendingDiscard = null;
    this.pendingKeep = null;
    this.focusIntent = false;
  },
  mounted() {
    // Statusfilter (Standard „Aktiv“) gleich beim Start setzen; gefiltert wird lokal
    this.applyFilter();
    this.loadRoles();
  },
  beforeRouteLeave(to, from, next) {
    // Abmelden (Profil bereits entfernt) und laufendes Speichern nicht aufhalten
    if (!this.dirty || this.saving || !this.$store.state.isLoggedIn) {
      next();
      return;
    }
    this.askDiscard(
      () => next(),
      () => next(false)
    );
  },
  methods: {
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance ? ref.instance : null;
    },
    updateGridDimensions() {
      const grid = this.grid();
      if (grid) {
        grid.updateDimensions();
      }
    },
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    // Status und Rolle als Prüffunktion auf dem Grid wie bei Verkäufen (jedes Mal eine neue Funktion); Spaltenfilter
    // wirken auf ausgeblendeten Spalten nicht
    emptyUserFilters() {
      return emptyFilters(USER_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "roles") {
        const role = (Array.isArray(this.roles) ? this.roles : []).find((r) => r.name === key);
        return (role && (role.description || role.name)) || key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._users || [], this.filters, this.filterLabel, USER_FILTER_CONFIG));
    },
    applyFilter() {
      const grid = this.grid();
      if (grid) {
        const match = buildPredicate(this.filters, USER_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
    },
    onFiltersInput(filters) {
      this.filters = filters || this.emptyUserFilters();
      this.applyFilter();
      this.refreshFacets();
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    exportToExcel() {
      // wie bisher alle acht Spalten in Definitionsreihenfolge mit dem aktuellen Filter; ohne die Bildschirmspalte „Status“
      exportWithColumns(this.$refs[this.gridRefName].instance, { exclude: ["status"] });
    },
    async loadRoles() {
      this.roles = (await axios.get(`${this.$store.state.baseUrl}role`)).data;
    },
    passwordText(status) {
      if (status === "CONFIRMED") {
        return "Gesetzt";
      }
      return status ? String(status) : "–";
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    onDataErrorOccurred(e) {
      // Speichern läuft nicht mehr über das Grid; hier landen nur Ladefehler
      if (e && e.error) {
        e.error.message = "Benutzer konnten nicht geladen werden.";
      }
    },
    // inaktive Benutzer blasser (Kundenwunsch 13.10.2026: „deutlicher hervorheben ob aktiv“)
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data) {
        return;
      }
      const el = e.rowElement && e.rowElement.get ? e.rowElement.get(0) : e.rowElement;
      if (el && el.classList) {
        el.classList.toggle("user-row--off", e.data.active != 1);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      this.updateCounts(grid);
      this.syncEditRow();
      this.$nextTick(this.highlightEditRow);
    },
    // Fußleiste: Anzahl nach Filter und Suche, davon mit offener Erstanmeldung (geladene Zeilen, derselbe Filter)
    updateCounts(grid) {
      const users = this._users;
      this.usersLoaded = Array.isArray(users);
      if (!this.usersLoaded) {
        return;
      }
      const total = Number(grid.totalCount());
      this.countTotal = total > 0 ? total : 0;
      this.resultCount = this.countTotal;
      let rows = users;
      const filter = grid.getCombinedFilter();
      if (filter) {
        try {
          rows = query(users).filter(filter).toArray();
        } catch (err) {
          rows = [];
        }
      }
      this.countFirstLogin = rows.filter((row) => row && row.password_status === "FORCE_CHANGE_PASSWORD").length;
    },

    // ---------------------------------------------------------------- Auswahl und Detailbereich
    // Klick/Tastatur auf eine andere Zeile: bei ungespeicherten Änderungen erst nachfragen
    onFocusedRowChanging(e) {
      if (this.saving) {
        e.cancel = true;
        return;
      }
      if (!this.dirty || e.newRowIndex === e.prevRowIndex) {
        this.markFocusIntent();
        return;
      }
      e.cancel = true;
      const index = e.newRowIndex;
      this.askDiscard(() => {
        const grid = this.grid();
        if (grid) {
          this.markFocusIntent();
          grid.option("focusedRowIndex", index);
        }
      });
    },
    onFocusedRowChanged(e) {
      const intent = this.focusIntent;
      this.focusIntent = false;
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.isEditRow(row.data)) {
        return;
      }
      if (!this.editMode || intent) {
        this.openEdit(row.data);
        return;
      }
      // DevExtreme hat den Fokus selbst verschoben (Filter, Suche, Sortierung, Blättern):
      // der Detailbereich bleibt beim bearbeiteten Benutzer
      this.$nextTick(this.restoreGridFocus);
    },
    // Fokuswechsel durch Klick/Tastatur; gilt nur für den laufenden Ablauf
    markFocusIntent() {
      this.focusIntent = true;
      Promise.resolve().then(() => {
        this.focusIntent = false;
      });
    },
    restoreGridFocus() {
      const grid = this.grid();
      if (!grid) {
        return;
      }
      if (this.editMode === "edit" && this.editRow) {
        const key = grid.keyOf(this.editRow);
        if (grid.getRowIndexByKey(key) >= 0) {
          grid.option("focusedRowKey", key);
          return;
        }
      }
      grid.option("focusedRowIndex", -1);
    },
    // bearbeiteter Benutzer wieder sichtbar (Filter oder Suche zurückgenommen): Zeile erneut markieren, ohne zu blättern
    highlightEditRow() {
      const grid = this.grid();
      if (!grid || this.editMode !== "edit" || !this.editRow) {
        return;
      }
      const current = grid.option("focusedRowKey");
      if (current !== undefined && current !== null) {
        return;
      }
      const key = grid.keyOf(this.editRow);
      if (grid.getRowIndexByKey(key) >= 0) {
        grid.option("focusedRowKey", key);
      }
    },
    isEditRow(data) {
      return (
        this.editMode === "edit" &&
        !!this.editRow &&
        !!data &&
        String(data.user_id) === String(this.editRow.user_id) &&
        data.username === this.editRow.username
      );
    },
    findUser(userId, username) {
      const users = Array.isArray(this._users) ? this._users : [];
      return users.find((u) => u && String(u.user_id) === String(userId) && u.username === username) || null;
    },
    // Entwurf beim Öffnen aus der Zeile kopieren (active, is_test_user als Boolean für die Schalter)
    openEdit(row) {
      const saved = Object.freeze(Object.assign({}, row));
      const draft = {
        username: saved.username,
        user_id: saved.user_id,
        fullname: textValue(saved.fullname),
        role: saved.role,
        comment: textValue(saved.comment),
        active: Number(saved.active) === 1,
        is_test_user: Number(saved.is_test_user) === 1,
      };
      this.editRow = saved;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "edit";
    },
    // nach dem Neuladen (z. B. Passwort geändert, Aktualisieren) den Detailbereich ohne offene Änderungen auffrischen
    syncEditRow() {
      if (this.editMode !== "edit" || !this.editRow || this.dirty || this.saving) {
        return;
      }
      const fresh = this.findUser(this.editRow.user_id, this.editRow.username);
      if (fresh && SYNC_FIELDS.some((key) => fresh[key] !== this.editRow[key])) {
        this.openEdit(fresh);
      }
    },
    nextUserId() {
      if (!Array.isArray(this._users)) {
        return "";
      }
      const ids = this._users.map((u) => Number(u && u.user_id)).filter((n) => Number.isFinite(n));
      return ids.length ? Math.max.apply(null, ids) + 1 : 1;
    },
    // Anlegen-Modus: Benutzer-ID vorbelegt (größte ID der vollständig geladenen Liste + 1), Rolle Fahrer
    openCreate() {
      const draft = emptyDraft();
      draft.user_id = this.nextUserId();
      this.editRow = null;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "create";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    addUser() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.openCreate());
        return;
      }
      this.openCreate();
    },
    closeDetail() {
      this.editMode = null;
      this.editRow = null;
      this.draft = emptyDraft();
      this.original = emptyDraft();
      this.errors = {};
      this.saveError = "";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailClose() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.closeDetail());
        return;
      }
      this.closeDetail();
    },
    onLayoutChange(e) {
      this.detailMode = e && e.open ? e.mode : null;
      this.applyScreenView();
      this.$nextTick(this.updateGridDimensions);
    },
    // Bemerkung bei Detail-Overlay ausblenden (Wert steht im Bearbeiten-Bereich)
    applyScreenView() {
      const grid = this.grid();
      if (!grid) {
        return;
      }
      const showComment = this.detailMode !== "overlay";
      if (grid.columnOption("comment", "visible") !== showComment) {
        grid.columnOption("comment", "visible", showComment);
      }
    },
    onField(key, value) {
      this.draft[key] = value;
      if (this.errors[key]) {
        const errors = Object.assign({}, this.errors);
        delete errors[key];
        this.errors = errors;
      }
      this.saveError = "";
    },
    revertChanges() {
      this.draft = Object.assign({}, this.original);
      this.errors = {};
      this.saveError = "";
    },
    validateCreate() {
      const draft = this.draft;
      const errors = {};
      if (!textValue(draft.username).trim()) {
        errors.username = "Pflichtfeld";
      }
      const id = textValue(draft.user_id).trim();
      if (!id) {
        errors.user_id = "Pflichtfeld";
      } else if (!/^\d+$/.test(id)) {
        errors.user_id = "Bitte eine ganze Zahl eingeben.";
      }
      if (!textValue(draft.fullname).trim()) {
        errors.fullname = "Pflichtfeld";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    // Speichern über dieselben Store-Funktionen wie das bisherige Batch-Editing (PUT bzw. POST user)
    async saveChanges() {
      if (!this.dirty || this.saving) {
        return;
      }
      const create = this.editMode === "create";
      if (create && !this.validateCreate()) {
        return;
      }
      const draft = Object.assign({}, this.draft);
      const row = this.editRow;
      let target;
      this.saving = true;
      this.saveError = "";
      try {
        if (create) {
          target = { user_id: Number(textValue(draft.user_id).trim()), username: draft.username };
          await this.userDataSource.insert({
            user_id: target.user_id,
            username: draft.username,
            fullname: draft.fullname,
            role: draft.role || "DRIVER",
            is_test_user: draft.is_test_user ? 1 : 0,
            active: 1,
          });
        } else {
          const changes = {};
          this.changedFields.forEach((key) => {
            changes[key] = BOOLEAN_FIELDS.indexOf(key) !== -1 ? (draft[key] ? 1 : 0) : draft[key];
          });
          target = { user_id: row.user_id, username: row.username };
          await this.userDataSource.update(
            { customer_id: row.customer_id, user_id: row.user_id, username: row.username },
            changes
          );
        }
      } catch (err) {
        this.saving = false;
        this.saveError = create
          ? "Benutzer-ID oder Benutzername bereits vergeben?"
          : "Änderungen konnten nicht gespeichert werden.";
        return;
      }
      // gespeichert: der Entwurf ist der neue Stand; neu laden (wie refresh-mode="full") und Auswahl wiederherstellen
      this.original = draft;
      const grid = this.grid();
      if (grid) {
        try {
          await grid.refresh();
        } catch (err) {
          // Ladefehler zeigt das Grid selbst
        }
      }
      this.saving = false;
      if (this._isDestroyed || this.dirty) {
        return;
      }
      const fresh = this.findUser(target.user_id, target.username);
      if (!fresh) {
        if (create) {
          this.closeDetail();
        }
        return;
      }
      // neuer Benutzer → Bearbeiten-Modus
      this.openEdit(fresh);
      const current = this.grid();
      if (create && current) {
        current.option("focusedRowKey", current.keyOf(fresh));
      }
    },

    // ---------------------------------------------------------------- Passwort
    changePassword() {
      this.passwortChangeItem = this.editRow;
      this.dialog = true;
    },
    async savePassword() {
      this.dialog = false;
      try {
        await axios.put(
          `${this.$store.state.baseUrl}password?customer_id=${this.passwortChangeItem.customer_id}&username=${this.passwortChangeItem.username}`,
          { newPassword: this.newPassword }
        );
      } catch (err) {
        this.showSnackbar("Passwort konnte nicht geändert werden.");
        return;
      }
      this.refreshDataGrid();
      this.newPassword = null;
    },

    // ---------------------------------------------------------------- Rückfrage „Änderungen verwerfen?“
    askDiscard(onDiscard, onKeep) {
      const previousKeep = this.pendingKeep;
      this.pendingDiscard = onDiscard;
      this.pendingKeep = onKeep || null;
      if (previousKeep) {
        previousKeep();
      }
      this.discardDialog = true;
    },
    confirmDiscard() {
      const action = this.pendingDiscard;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      this.revertChanges();
      if (action) {
        action();
      }
    },
    keepEditing() {
      const action = this.pendingKeep;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      if (action) {
        action();
      }
    },
    onDiscardDialogInput(open) {
      if (!open) {
        this.keepEditing();
      }
    },
    showSnackbar(text) {
      this.snackbarText = text;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* Grid-Spalte neben dem Detailbereich (AppPage mit Slot detail ist eine Flex-Zeile) */
.user-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Handy: natürliche Höhe statt Füllhöhe (ohne feste Höhe der Seite fiele flex: 1 1 0 auf 0 zusammen) */
.user-grid.user-grid--auto {
  flex: none;
}

/* Zellen (Vorlagen im Grid): Avatar 28, Name 13/500 mit Test-Tag, darunter Benutzername 12/16 */
.user-grid ::v-deep .user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.user-grid ::v-deep .user-cell__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.user-grid ::v-deep .user-cell__name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 500;
  color: var(--ink);
}
.user-grid ::v-deep .user-cell__fullname,
.user-grid ::v-deep .user-cell__login {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-grid ::v-deep .user-cell__login {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.user-grid ::v-deep .user-role {
  color: var(--ink-2);
}
.user-grid ::v-deep .user-pw {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ink-3);
}
.user-grid ::v-deep .user-pw--open {
  font-weight: 500;
  color: var(--amber);
}
.user-grid ::v-deep .user-muted {
  color: var(--ink-3);
}
/* Status: Punkt 8 (aktiv grün gefüllt, inaktiv grauer Ring), Text, Tag „Test“ */
.user-grid ::v-deep .user-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.user-grid ::v-deep .user-status__dot {
  flex: none;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
}
.user-grid ::v-deep .user-status__dot--on {
  background: var(--green-dot);
}
.user-grid ::v-deep .user-status__dot--off {
  border: 1.5px solid var(--ink-4);
}
.user-grid ::v-deep .user-status__text {
  font-weight: 500;
  color: var(--ink);
}
/* inaktive Benutzer blasser: Texte grau, Avatar halb durchsichtig */
.user-grid ::v-deep .dx-data-row.user-row--off .user-cell__name,
.user-grid ::v-deep .dx-data-row.user-row--off .user-role,
.user-grid ::v-deep .dx-data-row.user-row--off .user-status__text {
  color: var(--ink-3);
}
.user-grid ::v-deep .dx-data-row.user-row--off .app-avatar {
  opacity: .5;
}

/* Fuß des Detailbereichs: „1 Änderung“ links, Verwerfen und Speichern rechts */
/* eigene Zeile über den Knöpfen, über die volle Breite des Fußes (Polster 4 16 ausgeglichen), Trennlinie darunter */
.user-foot__text {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -4px -16px 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--line-2);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.user-foot__text--error {
  color: var(--red);
}

p.user-dialog__text {
  margin: 0;
}
</style>
